import { Button, LuIcon, toast } from "@overmap-ai/blocks"
import { selectOutboxRequests } from "@overmap-ai/core"
import { memo, useCallback, useEffect, useRef, useState } from "react"
import * as serviceWorkerRegistration from "src/serviceWorkerRegistration"

import { modelIconMapping } from "../../mappings/icons"
import { isNavigatingAway, sdk } from "../../sdk"
import { useAppSelector } from "../../store"
import { selectIsLoggedIn } from "../../store/selectors"

export const AppUpdater = memo(() => {
	const isLoggedIn = useAppSelector(selectIsLoggedIn)
	const [waitingWorker, setWaitingWorker] = useState<ServiceWorker | null>(null)
	const description = useToastDescription()
	const hasShownToast = useRef(false)
	const [updateApp, setUpdateApp] = useState(false)

	// NOTE: Don't change this to a simple useCallback. It results in getting spammed with update notifications.
	useEffect(() => {
		if (!updateApp) return
		setUpdateApp(false)

		void (async () => {
			// Sign out first, then let the new worker take over. Arming the `controllerchange` reload before the sign-out
			// let it win the race against `localforage.clear()`, and the page came back with the session and outbox intact.
			// The toast has already asked about the outbox, so `logout` must not ask again.
			try {
				await sdk.main.logout({ confirmed: true })
			} catch (error: unknown) {
				console.error("Could not sign out before updating; updating anyway.", error)
			}

			// A sign-out that got as far as navigating needs no reload: the page it returns to is a fresh load. The
			// listener is for a sign-out that failed before that.
			navigator.serviceWorker.addEventListener(
				"controllerchange",
				() => {
					if (!isNavigatingAway()) window.location.reload()
				},
				{ once: true },
			)

			// Tell the waiting SW to activate immediately
			waitingWorker?.postMessage({ type: "SKIP_WAITING" })
		})()
	}, [updateApp, waitingWorker])

	const onServiceWorkerUpdate = useCallback(
		(registration: ServiceWorkerRegistration) => {
			if (registration) setWaitingWorker(registration.waiting)
			if (hasShownToast.current) return
			hasShownToast.current = true

			if (isLoggedIn) {
				toast({
					title: "Update available",
					description,
					duration: Infinity,
					icon: <LuIcon icon={modelIconMapping.refresh} />,
					action: (
						<Button size="sm" onClick={() => setUpdateApp(true)}>
							Update
						</Button>
					),
				})
			} else {
				setUpdateApp(true)
			}
		},
		[description, isLoggedIn],
	)

	useEffect(() => {
		// register the service worker
		// the onUpdate callback only fires once
		serviceWorkerRegistration.register({ onUpdate: onServiceWorkerUpdate })

		// The dev server serves no worker (see `devOptions` in vite.config.ts), so `ready` below would never settle.
		// `register()` still runs first: in development it removes any worker left over from before that was decided.
		if (import.meta.env.DEV) {
			console.info("Local development: not waiting for a service worker, as the dev server does not serve one.")
			return
		}

		// Check if there is a pending update.
		// This runs every time the page is loaded, to account for the onUpdate callback only firing once.
		void navigator.serviceWorker.ready.then((registration) => {
			// if pending update, show toast
			if (registration.waiting) {
				onServiceWorkerUpdate(registration)
			}
		})
	}, [onServiceWorkerUpdate])

	return null
})

function useToastDescription(): string {
	const requests = useAppSelector(selectOutboxRequests)
	const itemsDescription = requests.length === 1 ? "one item" : `${requests.length} items`
	const outBoxMessage = `Updating may delete the ${itemsDescription} in your outbox and will sign you out.`

	const justSignOutMessage = "Updating will sign you out."
	const message = requests.length > 0 ? outBoxMessage : justSignOutMessage

	return `${message} Would you like to proceed with the update?`
}

AppUpdater.displayName = "AppUpdater"
